import axios from "axios";
import { toast } from "react-toastify";
import { useIntl } from "../contexts/IntlContext";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  searchEstablishmentsForFilters,
  TEstablishmentFilterOption,
} from "../services/establishmentsResolver";

const SEARCH_DEBOUNCE_MS = 300;

type UseEstablishmentSearchOptions = {
  country?: string;
  enabled: boolean;
  /** Chamado com cada página recebida, para o chamador manter um cache de nomes por id. */
  onResults?: (options: TEstablishmentFilterOption[]) => void;
};

/**
 * Busca de estabelecimentos sob demanda para um Autocomplete: carrega só a primeira página ao
 * abrir o campo e depois refaz a busca no servidor (debounce) conforme o usuário digita, em vez
 * de paginar `/app/establishments` inteiro ao montar a tela.
 */
export function useEstablishmentSearch({
  country,
  enabled,
  onResults,
}: UseEstablishmentSearchOptions) {
  const intl = useIntl();
  const [options, setOptions] = useState<TEstablishmentFilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const onResultsRef = useRef(onResults);
  onResultsRef.current = onResults;

  useEffect(() => {
    if (!enabled || !open) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchEstablishmentsForFilters({
          country,
          search,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setOptions(rows);
        onResultsRef.current?.(rows);
      } catch (err) {
        if (!controller.signal.aborted && !axios.isCancel(err)) {
          setOptions([]);
          toast.error(intl.formatMessage({ id: "messages.connectionError" }));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, search ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [country, enabled, open, search, intl]);

  const onInputChange = useCallback(
    (_: unknown, value: string, reason: string) => {
      setSearch(reason === "input" ? value : "");
    },
    [],
  );

  return {
    options,
    loading,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    onInputChange,
  };
}
